import { AttributionControl, Map as MapLibreMap, ScaleControl } from 'maplibre-gl'

import { getBasemap, type BasemapId } from './basemaps'
import { setInkTone } from './layers/ink'
import { LocationMarker } from './marker'
// Side effect: points MapLibre at its bundled worker. Must precede `new Map()`.
import './worker'

/**
 * Everything MapLibre. No Svelte imports here or anywhere under `lib/map/`:
 * overlay layers are imperative and must not depend on the UI framework.
 * `MapView.svelte` is the only bridge between this module and the component
 * tree.
 */

/** Continental US, the extent NWS covers. */
const HOME: [number, number] = [-98.5, 39.5]
const HOME_ZOOM = 3.6

export interface MapOptions {
  basemap: BasemapId
  cartoKey: string
  /**
   * Mobile: compact attribution. Neither layout uses MapLibre's zoom buttons:
   * a phone pinches, and the desktop draws its own (Phase 11 plan §7).
   */
  compact: boolean
  onError?: (message: string) => void
}

export interface Padding {
  top: number
  bottom: number
  left: number
  right: number
}

export interface MapHandle {
  readonly map: MapLibreMap
  setMarker(lat: number, lon: number): void
  clearMarker(): void
  flyTo(lat: number, lon: number, zoom?: number): void
  jumpTo(lat: number, lon: number, zoom?: number): void
  setBasemap(id: BasemapId, cartoKey: string): void
  /** Keeps `flyTo` centring on the map the app's own chrome leaves showing. */
  setPadding(padding: Padding): void
  /**
   * `point` is MapLibre's CSS-pixel position within the canvas, which is what
   * a WMS GetFeatureInfo query needs: `canvas.width` is *device* pixels and is
   * wrong by the device pixel ratio on every phone.
   */
  onClick(cb: (lat: number, lon: number, point: { x: number; y: number }) => void): () => void
  /** Fires when the camera starts moving, however it was started. */
  onMoveStart(cb: () => void): () => void
  /**
   * Re-add style-owned layers after a basemap switch. `LayerController`
   * subscribes to the map directly; this stays for anything above lib/map/
   * that needs the same signal.
   */
  onStyleLoad(cb: () => void): () => void
  /**
   * The camera's bearing and pitch, in degrees, whenever either changes —
   * the compass shows only while the map is not north-up and flat.
   */
  onRotate(cb: (bearing: number, pitch: number) => void): () => void
  /** Back to north-up and flat; `animate: false` under reduced motion. */
  resetNorth(animate: boolean): void
  resize(): void
  destroy(): void
}

export function createMap(container: HTMLElement, opts: MapOptions): MapHandle {
  const basemap = getBasemap(opts.basemap)
  setInkTone(basemap.tone)

  const map = new MapLibreMap({
    container,
    style: basemap.style(opts.cartoKey),
    center: HOME,
    zoom: HOME_ZOOM,
    minZoom: 2,
    maxZoom: basemap.maxZoom,
    attributionControl: false, // Replaced below so it can be compact on mobile.
  })

  map.addControl(new AttributionControl({ compact: opts.compact }), 'bottom-right')
  map.addControl(new ScaleControl({ unit: 'imperial' }), 'bottom-left')

  // A WebGL2 failure or an unreachable tile host arrives here, not as a throw.
  map.on('error', (e) => opts.onError?.(e.error?.message ?? 'Map error'))

  const marker = new LocationMarker(map)
  let padding: Padding = { top: 0, bottom: 0, left: 0, right: 0 }

  const padded = () => ({ ...padding })

  return {
    map,

    setMarker(lat, lon) {
      marker.set(lat, lon)
    },

    clearMarker() {
      marker.clear()
    },

    flyTo(lat, lon, zoom) {
      map.flyTo({ center: [lon, lat], zoom, padding: padded(), duration: 800 })
    },

    jumpTo(lat, lon, zoom) {
      map.jumpTo({ center: [lon, lat], zoom, padding: padded() })
    },

    setBasemap(id, cartoKey) {
      const next = getBasemap(id)
      // Before setStyle: every vector layer is re-added on the new style's
      // style.load, and reads its ink as it goes.
      setInkTone(next.tone)
      map.setMaxZoom(next.maxZoom)
      map.setStyle(next.style(cartoKey))
    },

    setPadding(next) {
      const px = (n: number) => Math.max(0, Math.round(n))
      padding = { top: px(next.top), bottom: px(next.bottom), left: px(next.left), right: px(next.right) }
      // Only stored; applying it to the current view would jump the map under
      // the user's finger.
    },

    onClick(cb) {
      const handler = (e: {
        lngLat: { lat: number; lng: number }
        point: { x: number; y: number }
      }) => cb(e.lngLat.lat, e.lngLat.lng, e.point)
      map.on('click', handler)
      return () => map.off('click', handler)
    },

    onMoveStart(cb) {
      map.on('movestart', cb)
      map.on('zoomstart', cb)
      return () => {
        map.off('movestart', cb)
        map.off('zoomstart', cb)
      }
    },

    onStyleLoad(cb) {
      map.on('style.load', cb)
      return () => map.off('style.load', cb)
    },

    onRotate(cb) {
      const handler = () => cb(map.getBearing(), map.getPitch())
      map.on('rotate', handler)
      map.on('pitch', handler)
      return () => {
        map.off('rotate', handler)
        map.off('pitch', handler)
      }
    },

    resetNorth(animate) {
      // No padding here: the centre stays where it is, only the camera turns.
      map.easeTo({ bearing: 0, pitch: 0, duration: animate ? 400 : 0 })
    },

    resize() {
      map.resize()
    },

    destroy() {
      marker.clear()
      map.remove()
    },
  }
}
